import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faPercent, faLink, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';

const NewSimulationTriggersManagement = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    trigger_touchup_pct: '',
    trigger_overcoat_pct: '',
    trigger_recoat_pct: '',
    is_active: true
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/new-simulation-triggers');
      if (response.data.success) {
        setConfigs(response.data.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch trigger configurations. Ensure the new_simulation_trigger_config table exists.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingConfig) return;
    try {
      setSaving(true);
      await api.put(`/new-simulation-triggers/${editingConfig.id}`, {
        trigger_touchup_pct: formData.trigger_touchup_pct !== '' ? parseFloat(formData.trigger_touchup_pct) : undefined,
        trigger_overcoat_pct: formData.trigger_overcoat_pct !== '' ? parseFloat(formData.trigger_overcoat_pct) : undefined,
        trigger_recoat_pct: formData.trigger_recoat_pct !== '' ? parseFloat(formData.trigger_recoat_pct) : undefined,
        is_active: formData.is_active
      });
      await fetchConfigs();
      setShowModal(false);
      resetForm();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      trigger_touchup_pct: config.trigger_touchup_pct != null ? String(config.trigger_touchup_pct) : '',
      trigger_overcoat_pct: config.trigger_overcoat_pct != null ? String(config.trigger_overcoat_pct) : '',
      trigger_recoat_pct: config.trigger_recoat_pct != null ? String(config.trigger_recoat_pct) : '',
      is_active: config.is_active !== false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      trigger_touchup_pct: '',
      trigger_overcoat_pct: '',
      trigger_recoat_pct: '',
      is_active: true
    });
    setEditingConfig(null);
  };

  const filteredConfigs = configs.filter(config => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      (config.combination_code && config.combination_code.toLowerCase().includes(s)) ||
      (config.corrosion_category_code && config.corrosion_category_code.toLowerCase().includes(s)) ||
      (config.damage_severity_code && config.damage_severity_code.toLowerCase().includes(s))
    );
  });

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-gray-600">Loading trigger configurations...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">New Simulation Triggers</h1>
        <p className="text-gray-600 mt-1">
          Tune TU (Touch-up), OC (Overcoat), and RE (Recoat) decay thresholds per corrosion-damage combination. Used by the New Simulation Engine. Rows are created automatically when combinations are added.
        </p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="mb-6">
        <input
          type="text"
          placeholder="Search by combination code, category, or severity..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Combination</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Touch-up %</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Overcoat %</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Recoat %</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredConfigs.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                  No trigger configurations found. Run the New Simulation Engine SQL to create the table, and add combinations (they will get default trigger rows automatically).
                </td>
              </tr>
            ) : (
              filteredConfigs.map(config => (
                <tr key={config.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faLink} className="h-5 w-5 text-blue-500 mr-2" />
                      <span className="text-sm font-medium text-gray-900">{config.combination_code}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faShieldAlt} className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900">{config.corrosion_category_code}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {config.damage_severity_code || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className="text-sm text-gray-900">{config.trigger_touchup_pct}%</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className="text-sm text-gray-900">{config.trigger_overcoat_pct}%</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <span className="text-sm text-gray-900">{config.trigger_recoat_pct}%</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      config.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {config.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(config)}
                      className="text-indigo-600 hover:text-indigo-900"
                      title="Edit thresholds"
                    >
                      <FontAwesomeIcon icon={faPencil} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && editingConfig && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Edit triggers – {editingConfig.combination_code}
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Touch-up threshold (%)
                </label>
                <input
                  type="number"
                  name="trigger_touchup_pct"
                  value={formData.trigger_touchup_pct}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. 1.0"
                />
                <p className="mt-1 text-xs text-gray-500">Decay % to trigger Touch-up (TU)</p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Overcoat threshold (%)
                </label>
                <input
                  type="number"
                  name="trigger_overcoat_pct"
                  value={formData.trigger_overcoat_pct}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. 1.5"
                />
                <p className="mt-1 text-xs text-gray-500">Decay % to trigger Overcoat (OC)</p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recoat threshold (%)
                </label>
                <input
                  type="number"
                  name="trigger_recoat_pct"
                  value={formData.trigger_recoat_pct}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. 2.0"
                />
                <p className="mt-1 text-xs text-gray-500">Decay % to trigger Recoat/Full (RE)</p>
              </div>
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="mr-2 rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-500 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewSimulationTriggersManagement;
